import get from "lodash/get";
import isNil from "lodash/isNil";
import { useActivityFormStore } from "PFApp/activities/activity_form/activity_form_store";
import { usePopulateLinkedEngagementFields } from "PFApp/activities/activity_form/hooks/engagement_link/use_populate_linked_engagement_fields";
import { useEngagementScopeForm } from "PFApp/activities/activity_form/hooks/use_engagement_scope_form";
import { useSubtemplate } from "PFApp/activities/activity_form/hooks/use_subtemplate";
import { EngagementItem } from "PFApp/components/engagement_item";
import {
  FIELD_PARENT_ACTIVITY_KEY,
  FIELD_PRIVACY_KEY
} from "PFApp/constants/activity_form/activity_form_field_keys";
import { TemplateKey } from "PFApp/constants/templates";
import { getPrivacyConfiguration } from "PFApp/helpers/activity_form/get_privacy_configuration";
import { useTemplate } from "PFApp/hooks";
import { useFormValidation } from "PFApp/hooks/activity_form/use_form_validation";
import { ReadOnlyField } from "PFComponents/read_only_field";
import { Select, SelectProps } from "PFComponents/select";
import { fetchActivities } from "PFCore/services/activities";
import { Activity } from "PFTypes";
import { Option } from "PFTypes/options";
import { useCallback } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";

export const EngagementSelector = () => {
  const { t } = useTranslation("activities");

  const {
    control,
    formState: { errors },
    setValue
  } = useFormContext();

  const template = useActivityFormStore((state) => state.template);
  const parentActivity = useActivityFormStore((state) => state.parentActivity);
  const { draft: submittedAsDraft } = useActivityFormStore((state) => state.submitOptions);
  const { isAudit } = useActivityFormStore((state) => state.activityStatus);

  const engagementKey = isAudit ? TemplateKey.AuditEngagement : TemplateKey.Engagement;
  const engagementTemplate = useTemplate({ key: engagementKey });
  const subtemplate = useSubtemplate();
  const { isForceVisibilitySet } = getPrivacyConfiguration({ template, parentActivity });

  const { isEngagementScope } = useEngagementScopeForm();
  const { isFieldRequired } = useFormValidation({ subtemplate, template });

  const setEngagementValues = usePopulateLinkedEngagementFields();

  const parseEngagementToOption = useCallback(
    (activity: Activity) => ({
      id: activity.id,
      name: activity.name,
      isPrivate: activity.private
    }),
    []
  );

  const fetchOptions = useCallback<NonNullable<SelectProps<Activity>["fetchOptions"]>>(
    async ({ ids, searchTerm }) => {
      const filters = ids?.length
        ? { fields: { id: ids } }
        : {
            fields: {
              term: searchTerm,
              template_key: engagementTemplate?.key,
              state: ["draft", "new"]
            }
          };
      const response = await fetchActivities({
        filters
      });
      const options = response.entries.map((entry) => ({
        id: entry.activity.id,
        value: entry.activity.name,
        displayElement: <EngagementItem item={parseEngagementToOption(entry.activity)} />,
        original: entry.activity
      }));
      return {
        ...response,
        entries: options
      };
    },
    [engagementTemplate?.key, parseEngagementToOption]
  );

  const handleChange = (engagement: Activity | null, onChange: (engagement: Activity | null) => void) => {
    if (engagement && !isForceVisibilitySet && template.default_visibility === "inherit_from_parent") {
      setValue(FIELD_PRIVACY_KEY, engagement.private, { shouldDirty: true });
    }

    engagement && setEngagementValues({ engagement });
    onChange(engagement);
  };

  const required = isFieldRequired({ name: FIELD_PARENT_ACTIVITY_KEY });

  return (
    <Controller
      name={FIELD_PARENT_ACTIVITY_KEY}
      control={control}
      rules={{
        validate: (value: string) => {
          if (!submittedAsDraft && required && !value) {
            return t("form.errors.cantBeBlank");
          }
          return true;
        }
      }}
      render={({ field }) =>
        isEngagementScope ? (
          <ReadOnlyField label={t(`templates.${engagementKey}`)} value={field.value?.name ?? ""} />
        ) : (
          <Select<Activity>
            id={"parent_activity_select"}
            label={t(`templates.${engagementKey}`)}
            title={field.value?.name}
            renderDisplayValue={(value) => value?.displayElement}
            fetchOptions={fetchOptions}
            qaId="ActivitySelectionEngagement"
            required={required}
            value={field.value?.id}
            onChange={(option: Option<Activity>) => handleChange(option.original, field.onChange)}
            error={get(errors, field.name)?.message as string}
            onClear={!isNil(field.value) ? () => handleChange(null, field.onChange) : undefined}
          />
        )
      }
    />
  );
};
